import { useState } from 'react';
import { API_BASE, NewListing } from '../api';

/**
 * The code a prospect copies.
 *
 * Generated from the SAME values the page just sent, not from a static template
 * with placeholders. A snippet that needs three substitutions before it runs is a
 * snippet nobody runs, and the gap between "what the demo did" and "what the
 * docs say" is where integrations go wrong.
 *
 * The key is NOT interpolated. Every sample reads it from an environment
 * variable instead, because these get pasted into Slack, tickets and commit
 * messages, and a snippet that carries a live credential inside it will
 * eventually leak one. The demo key is low-value; the habit is not.
 */

export type Language = 'curl' | 'python' | 'node' | 'php';

export interface Snippets {
  curl: string;
  python: string;
  node: string;
  php: string;
}

const LABELS: Record<Language, string> = {
  curl: 'curl',
  python: 'Python',
  node: 'Node',
  php: 'PHP',
};

/** Absolute at build time, or the page's own origin in local dev where Vite
 *  proxies /v1 — either way the snippet a visitor copies is one that resolves. */
function base(): string {
  return API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');
}

function jsonBlock(value: unknown, indent = 2): string {
  return JSON.stringify(value, null, indent);
}

/** Shell-safe single-quoted string. */
function shellQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

export function searchSnippet(query: string): Snippets {
  const url = `${base()}/v1/search`;
  const payload = {
    query,
    limit: 20,
    options: { include_fields: true, explain: true },
  };

  return {
    curl: `curl -sS -X POST ${url} \\
  -H "Authorization: Bearer $PROPERTYSTACK_KEY" \\
  -H "Content-Type: application/json" \\
  -d ${shellQuote(jsonBlock(payload, 2))}`,

    python: `import os, requests

response = requests.post(
    "${url}",
    headers={"Authorization": f"Bearer {os.environ['PROPERTYSTACK_KEY']}"},
    json=${jsonBlock(payload, 4).replace(/\n/g, '\n    ').replace(/true/g, 'True').replace(/false/g, 'False')},
    timeout=30,
)
response.raise_for_status()
body = response.json()

# Ids and evidence come back; hydrate the rest from your own database.
for result in body["results"]:
    reasons = ", ".join(r["label"] for r in result["matchReasons"])
    print(result["doc_id"], round(result["score"], 3), reasons)`,

    node: `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.PROPERTYSTACK_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(${jsonBlock(payload, 2).replace(/\n/g, '\n  ')}),
});

if (!response.ok) throw new Error(\`search failed: \${response.status}\`);
const body = await response.json();

for (const result of body.results) {
  const reasons = result.matchReasons.map((r) => r.label).join(", ");
  console.log(result.doc_id, result.score.toFixed(3), reasons);
}`,

    php: `<?php
$payload = ${phpArray(payload, 0)};

$ch = curl_init("${url}");
curl_setopt_array($ch, [
    CURLOPT_POST           => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER     => [
        "Authorization: Bearer " . getenv("PROPERTYSTACK_KEY"),
        "Content-Type: application/json",
    ],
    CURLOPT_POSTFIELDS     => json_encode($payload),
    CURLOPT_TIMEOUT        => 30,
]);

$body = json_decode(curl_exec($ch), true);
curl_close($ch);

foreach ($body["results"] as $result) {
    $reasons = implode(", ", array_column($result["matchReasons"], "label"));
    echo $result["doc_id"], " ", round($result["score"], 3), " ", $reasons, PHP_EOL;
}`,
  };
}

export function ingestSnippet(listing: NewListing): Snippets {
  const url = `${base()}/v1/documents:batch`;
  const payload = { documents: [listing] };

  return {
    // Pretty-printed rather than minified: a single-quoted shell string may span
    // lines, and the one-line form put a 400-character JSON blob on one line —
    // unreadable in the panel, and the reason the code block used to widen the
    // whole page as the form was filled in.
    curl: `curl -sS -X POST ${url} \\
  -H "Authorization: Bearer $PROPERTYSTACK_KEY" \\
  -H "Content-Type: application/json" \\
  -d ${shellQuote(jsonBlock(payload, 2))}`,

    python: `import os, requests

# Up to 500 documents per call. Send your whole feed in batches; the response
# reports each one separately, so one bad record never fails the rest.
response = requests.post(
    "${url}",
    headers={"Authorization": f"Bearer {os.environ['PROPERTYSTACK_KEY']}"},
    json=${jsonBlock(payload, 4).replace(/\n/g, '\n    ').replace(/true/g, 'True').replace(/false/g, 'False').replace(/null/g, 'None')},
    timeout=30,
)
response.raise_for_status()

# 202: accepted for indexing. Embedding happens on a worker, so a document is
# searchable shortly after this returns, not at the instant it does.
for result in response.json()["results"]:
    print(result["doc_id"], result["status"], result.get("reason", ""))`,

    node: `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.PROPERTYSTACK_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(${jsonBlock(payload, 2).replace(/\n/g, '\n  ')}),
});

// 202 with a per-document verdict, not 200: one malformed listing is reported
// as rejected and the rest of the batch still lands.
const body = await response.json();
for (const result of body.results) {
  console.log(result.doc_id, result.status, result.reason ?? "");
}`,

    php: `<?php
$payload = ${phpArray(payload, 0)};

$ch = curl_init("${url}");
curl_setopt_array($ch, [
    CURLOPT_POST           => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER     => [
        "Authorization: Bearer " . getenv("PROPERTYSTACK_KEY"),
        "Content-Type: application/json",
    ],
    CURLOPT_POSTFIELDS     => json_encode($payload),
    CURLOPT_TIMEOUT        => 30,
]);

$body = json_decode(curl_exec($ch), true);
curl_close($ch);

foreach ($body["results"] as $result) {
    echo $result["doc_id"], " ", $result["status"], " ", $result["reason"] ?? "", PHP_EOL;
}`,
  };
}

/** PHP array literal. Written out rather than emitting json_decode('...') so the
 *  sample reads like PHP a developer would have written. */
function phpArray(value: unknown, depth: number): string {
  const pad = '    '.repeat(depth + 1);
  const closePad = '    '.repeat(depth);

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return `[\n${value.map((v) => `${pad}${phpArray(v, depth + 1)}`).join(',\n')},\n${closePad}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '[]';
    return `[\n${entries
      .map(([k, v]) => `${pad}"${k}" => ${phpArray(v, depth + 1)}`)
      .join(',\n')},\n${closePad}]`;
  }
  if (typeof value === 'string') return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

export default function CodeSamples({ snippets }: { snippets: Snippets }) {
  const [language, setLanguage] = useState<Language>('curl');
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippets[language]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access needs a secure context and can be refused. The code is
      // selectable on the page regardless, so this is not worth an error state.
    }
  }

  return (
    <div>
      <div className="code-head">
        <div className="langs">
          {(Object.keys(LABELS) as Language[]).map((lang) => (
            <button
              key={lang}
              type="button"
              className={lang === language ? 'active' : ''}
              onClick={() => setLanguage(lang)}
            >
              {LABELS[lang]}
            </button>
          ))}
        </div>
        <button type="button" className="link" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <pre className="code">
        <code>{snippets[language]}</code>
      </pre>

      <p className="footnote">
        The key is read from <code>PROPERTYSTACK_KEY</code> rather than pasted into the snippet —
        these get shared, and a credential inside one eventually leaks.
      </p>
    </div>
  );
}
